import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Calendar,
  FileText,
  Printer,
  Search,
  Send,
  Building2,
  User,
} from 'lucide-react';
import api from '../../utils/api';
import { compareSsCode } from '../../utils/ssCodeSort';
import { loadIssuedPaperworkPreview } from '../../utils/documentPreview';
import PageHeading from '../../components/PageHeading';
import DocumentMailStatus from '../../components/DocumentMailStatus';

const PRIORITIES = [
  { id: 'worker', label: 'Worker' },
  { id: 'building', label: 'Building' },
  { id: 'date', label: 'Date' },
  { id: 'form', label: 'By form' },
  { id: 'ss', label: 'By SS' },
];

const FORM_OPTS = [
  { id: 'FORM_12A', label: '12A' },
  { id: 'S_RAD', label: 'S-RaD' },
  { id: 'DEFECT_NOTICE', label: 'Defect report' },
  { id: 'INSPECTION_REPORT', label: 'Inspection report' },
  { id: 'SERVICE_REPORT', label: 'Service report' },
  { id: 'REINSPECTION_REPORT', label: 'Re-inspection report' },
];

function typeBadgeClass(type) {
  if (type === 'FORM_12A') return 'bg-emerald-100 text-emerald-800';
  if (type === 'S_RAD') return 'bg-amber-100 text-amber-900';
  if (type === 'DEFECT_NOTICE') return 'bg-red-100 text-red-800';
  if (type === 'INSPECTION_REPORT') return 'bg-sky-100 text-sky-900';
  return 'bg-slate-100 text-slate-700';
}

export default function IssuedForms() {
  const [phase, setPhase] = useState('search'); // search | results | detail | preview
  const [priority, setPriority] = useState('building');
  const [buildingName, setBuildingName] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [formTypes, setFormTypes] = useState([]);
  const [ssCodes, setSsCodes] = useState([]);
  const [workerName, setWorkerName] = useState('');
  const [ssOpen, setSsOpen] = useState(false);
  const [textFilter, setTextFilter] = useState('');
  const [searchParams, setSearchParams] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [sendTo, setSendTo] = useState('');
  const [sendBusy, setSendBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState('');

  const { data: ssList = [] } = useQuery({
    queryKey: ['workers-ss-codes'],
    queryFn: async () => {
      const res = await api.get('/workers/ss-codes');
      const list = res.data?.data || res.data || [];
      return Array.isArray(list) ? list : [];
    },
    staleTime: 300_000,
  });

  const sortedSsCatalog = useMemo(
    () => [...ssList].sort((a, b) => compareSsCode(a.code || a.ss_code, b.code || b.ss_code)),
    [ssList]
  );

  const sortedSelectedSs = useMemo(
    () => [...ssCodes].sort((a, b) => compareSsCode(a, b)),
    [ssCodes]
  );

  const resultsQuery = useQuery({
    queryKey: ['issued-forms', searchParams],
    enabled: !!searchParams && phase !== 'search',
    queryFn: async () => {
      const res = await api.get('/paperwork/issued-forms', { params: searchParams });
      return res.data?.data || res.data;
    },
    placeholderData: (prev) => prev,
  });

  const detailQuery = useQuery({
    queryKey: ['issued-form', selectedId],
    enabled: !!selectedId && (phase === 'detail' || phase === 'preview'),
    queryFn: async () => {
      const res = await api.get(`/paperwork/issued-forms/${selectedId}`);
      return res.data?.data || res.data;
    },
    placeholderData: (prev) => prev,
  });

  const items = resultsQuery.data?.items || [];
  const filtered = useMemo(() => {
    const q = textFilter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((row) =>
      [
        row.building_name,
        row.building_address,
        row.ss_code,
        row.ss_name,
        row.form_short,
        row.paperwork_label,
        row.worker_name,
        row.issued_date_display,
        row.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [items, textFilter]);

  const toggleForm = (id) => {
    setFormTypes((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSs = (code) => {
    setSsCodes((prev) => (prev.includes(code) ? prev.filter((x) => x !== code) : [...prev, code]));
  };

  const buildSearchParams = useCallback(
    (priorityId) => ({
      priority: priorityId,
      ...(buildingName.trim() ? { buildingName: buildingName.trim() } : {}),
      ...(dateFrom.trim() ? { dateFrom: dateFrom.trim() } : {}),
      ...(dateTo.trim() ? { dateTo: dateTo.trim() } : {}),
      ...(formTypes.length ? { formTypes: formTypes.join(',') } : {}),
      ...(ssCodes.length ? { ssCodes: ssCodes.join(',') } : {}),
      ...(workerName.trim() ? { workerName: workerName.trim() } : {}),
    }),
    [buildingName, dateFrom, dateTo, formTypes, ssCodes, workerName]
  );

  const runSearch = useCallback(
    (priorityOverride) => {
      const nextPriority = priorityOverride || priority;
      if (priorityOverride) setPriority(priorityOverride);
      setTextFilter('');
      setSelectedId(null);
      setPreviewHtml('');
      setActionMsg('');
      setSearchParams(buildSearchParams(nextPriority));
      setPhase('results');
    },
    [priority, buildSearchParams]
  );

  const openDetail = (id) => {
    setSelectedId(id);
    setPhase('detail');
    setPreviewHtml('');
    setActionMsg('');
  };

  const loadPreview = useCallback(async () => {
    if (!selectedId) {
      setActionMsg('No form selected.');
      return;
    }
    setPhase('preview');
    setPreviewHtml('');
    try {
      const preview = await loadIssuedPaperworkPreview(api, { recordId: selectedId });
      setPreviewHtml(preview.html || '');
    } catch (e) {
      setActionMsg(e.response?.data?.error || e.message || 'Preview failed');
      setPhase('detail');
    }
  }, [selectedId]);

  const returnHome = () => {
    setPhase('search');
    setSelectedId(null);
    setPreviewHtml('');
    setActionMsg('');
  };

  const handleSend = async () => {
    if (!selectedId) {
      setActionMsg('No document to send.');
      return;
    }
    if (!sendTo.trim()) {
      setActionMsg('Enter recipient email.');
      return;
    }
    setSendBusy(true);
    setActionMsg('');
    try {
      await api.post(`/paperwork/issued-forms/${selectedId}/send`, { to: sendTo.trim() });
      setSendBusy(false);
      returnHome();
    } catch (e) {
      setSendBusy(false);
      setActionMsg(e.response?.data?.error || e.message || 'Send failed');
    }
  };

  const handlePrint = () => {
    if (!previewHtml) {
      loadPreview().then(() => {});
      setActionMsg('Loading preview — press Print again when ready.');
      return;
    }
    const w = window.open('', '_blank');
    if (!w) {
      setActionMsg('Popup blocked — allow popups to print.');
      return;
    }
    w.document.write(previewHtml);
    w.document.close();
    w.focus();
    w.print();
    returnHome();
  };

  useEffect(() => {
    if (phase === 'preview' && selectedId && !previewHtml) {
      loadPreview();
    }
  }, [phase, selectedId, previewHtml, loadPreview]);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <PageHeading
        title="Issued document"
        infoTitle="Issued document"
        infoChildren={
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              Search issued 12A, S-RaD, Defect, and Inspection reports. Empty fields mean{' '}
              <strong>all</strong>.
            </p>
            <p>
              Header priority buttons (Worker / Building / Date / By form / By SS) run a search with the
              values already entered below, sorted by that priority.
            </p>
            <p>Defect reports link to later repair forms (and the reverse).</p>
          </div>
        }
        subPages={PRIORITIES.map((p) => ({
          id: p.id,
          label: p.label,
          active: priority === p.id && phase === 'results',
          onClick: () => runSearch(p.id),
        }))}
      />

      {phase === 'search' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Set filters below (empty = all), then use the header priority row or Search.
          </p>
          <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-5">
            <div>
              <label className="flex items-center gap-1.5 text-xs font-bold uppercase text-slate-500">
                <User className="h-3.5 w-3.5" /> Worker name
              </label>
              <input
                type="text"
                value={workerName}
                onChange={(e) => setWorkerName(e.target.value)}
                placeholder="All workers"
                className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm"
              />
            </div>

            <div>
              <label className="flex items-center gap-1.5 text-xs font-bold uppercase text-slate-500">
                <Building2 className="h-3.5 w-3.5" /> Building name
              </label>
              <input
                type="text"
                value={buildingName}
                onChange={(e) => setBuildingName(e.target.value)}
                placeholder="All buildings"
                className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-1.5 text-xs font-bold uppercase text-slate-500">
                  <Calendar className="h-3.5 w-3.5" /> From
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-3 text-sm min-h-[2.75rem]"
                />
              </div>
              <div>
                <label className="flex items-center gap-1.5 text-xs font-bold uppercase text-slate-500">
                  <Calendar className="h-3.5 w-3.5" /> To
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-3 text-sm min-h-[2.75rem]"
                />
              </div>
            </div>

            <div>
              <label className="flex items-center gap-1.5 text-xs font-bold uppercase text-slate-500">
                <FileText className="h-3.5 w-3.5" /> Form type
              </label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {FORM_OPTS.map((f) => {
                  const on = formTypes.includes(f.id);
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => toggleForm(f.id)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border ${
                        on
                          ? 'bg-navy-900 text-white border-navy-900'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-1">None selected = all</p>
            </div>

            <div>
              <label className="text-xs font-bold uppercase text-slate-500">SS</label>
              <button
                type="button"
                onClick={() => setSsOpen(true)}
                className="mt-1.5 w-full sm:w-auto px-4 py-2.5 rounded-lg border border-gray-200 bg-slate-50 text-sm font-semibold text-blue-900 hover:bg-slate-100"
              >
                Select SS…
              </button>
              <div className="mt-2 flex flex-wrap gap-1.5 min-h-[1.5rem]">
                {sortedSelectedSs.length ? (
                  sortedSelectedSs.map((c) => (
                    <span
                      key={c}
                      className="inline-flex px-2 py-0.5 rounded-md bg-slate-100 text-xs font-medium text-slate-800"
                    >
                      {c}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-gray-400">All SS</span>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={() => runSearch()}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-navy-900 text-white text-sm font-semibold hover:bg-navy-800"
            >
              <Search className="h-4 w-4" /> Search
            </button>
          </section>
        </div>
      )}

      {phase === 'results' && (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => setPhase('search')}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-800 hover:underline"
          >
            <ArrowLeft className="h-4 w-4" /> Back to search
          </button>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="search"
              value={textFilter}
              onChange={(e) => setTextFilter(e.target.value)}
              placeholder="Search in results…"
              className="w-full rounded-xl border border-gray-200 pl-10 pr-3 py-2.5 text-sm"
            />
          </div>
          {resultsQuery.isLoading && !items.length && (
            <p className="text-sm text-gray-500">Searching…</p>
          )}
          {resultsQuery.isError && (
            <p className="text-sm text-red-700 bg-red-50 rounded-lg p-3">
              {resultsQuery.error?.response?.data?.error || resultsQuery.error?.message || 'Search failed'}
            </p>
          )}
          <p className="text-xs text-gray-500">{filtered.length} form{filtered.length === 1 ? '' : 's'}</p>
          <ul className="space-y-2">
            {filtered.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => openDetail(row.id)}
                  className="w-full text-left bg-white rounded-xl border border-gray-200 p-4 hover:border-blue-300 hover:shadow-sm transition"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{row.building_name}</p>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {row.ss_code}
                        {row.ss_name ? ` · ${row.ss_name}` : ''}
                      </p>
                      {row.worker_name && (
                        <p className="text-xs text-gray-500 mt-1">IQP: {row.worker_name}</p>
                      )}
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-1.5">
                      <span
                        className={`text-[11px] font-bold px-2 py-1 rounded-md ${typeBadgeClass(
                          row.paperwork_type
                        )}`}
                      >
                        {row.form_short}
                      </span>
                      {row.mail_status ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-gray-600">
                          <DocumentMailStatus status={row.mail_status} className="w-3.5 h-3.5" />
                          {row.mail_to ? (
                            <span className="truncate max-w-[8rem]" title={row.mail_to}>
                              {row.mail_to}
                            </span>
                          ) : null}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    {row.issued_date_display}
                    {row.status && row.status !== 'ACTIVE' ? ` · ${row.status}` : ''}
                  </p>
                </button>
              </li>
            ))}
            {!resultsQuery.isLoading && !filtered.length && (
              <li className="text-center text-sm text-gray-500 py-10">No issued forms match.</li>
            )}
          </ul>
        </div>
      )}

      {(phase === 'detail' || phase === 'preview') && (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => (phase === 'preview' ? setPhase('detail') : setPhase('results'))}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-800 hover:underline"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>

          {detailQuery.isLoading && !detailQuery.data?.item && (
            <p className="text-sm text-gray-500">Loading…</p>
          )}
          {detailQuery.data?.item && (
            <>
              <section className="bg-white rounded-xl border border-gray-200 p-5">
                <p className="text-xl font-semibold text-gray-900">{detailQuery.data.item.building_name}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {detailQuery.data.item.ss_code}
                  {detailQuery.data.item.ss_name ? ` — ${detailQuery.data.item.ss_name}` : ''}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span
                    className={`text-xs font-bold px-2 py-1 rounded-md ${typeBadgeClass(
                      detailQuery.data.item.paperwork_type
                    )}`}
                  >
                    {detailQuery.data.item.form_short}
                  </span>
                  <span className="text-sm text-gray-600">{detailQuery.data.item.issued_date_display}</span>
                  {detailQuery.data.item.worker_name && (
                    <span className="text-sm text-gray-500">· {detailQuery.data.item.worker_name}</span>
                  )}
                </div>

                {!!detailQuery.data.linked?.chain?.length ? (
                  <div className="mt-4">
                    <p className="text-xs font-bold uppercase text-slate-500 mb-1">
                      Related flow · Inspection → Defect → Service → Certi
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {detailQuery.data.linked.chain.map((c, i) => (
                        <span key={c.id} className="inline-flex items-center gap-1">
                          {i > 0 ? <span className="text-slate-300 text-sm">→</span> : null}
                          <button
                            type="button"
                            className={`text-sm font-semibold underline ${
                              c.is_current ? 'text-primary-900' : 'text-blue-800'
                            }`}
                            onClick={() => openDetail(c.id)}
                          >
                            {c.label}
                            {c.issued_date_display ? ` · ${c.issued_date_display}` : ''}
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    {detailQuery.data.linked?.inspection_report && (
                      <p className="mt-4 text-sm">
                        Inspection report:{' '}
                        <button
                          type="button"
                          className="text-blue-800 font-semibold underline"
                          onClick={() => openDetail(detailQuery.data.linked.inspection_report.id)}
                        >
                          {detailQuery.data.linked.inspection_report.form_short} ·{' '}
                          {detailQuery.data.linked.inspection_report.issued_date_display}
                        </button>
                      </p>
                    )}
                    {detailQuery.data.linked?.primary_form && (
                      <p className="mt-4 text-sm">
                        Companion form:{' '}
                        <button
                          type="button"
                          className="text-blue-800 font-semibold underline"
                          onClick={() => openDetail(detailQuery.data.linked.primary_form.id)}
                        >
                          {detailQuery.data.linked.primary_form.form_short} ·{' '}
                          {detailQuery.data.linked.primary_form.issued_date_display}
                        </button>
                      </p>
                    )}
                    {detailQuery.data.linked?.defect && (
                      <p className="mt-4 text-sm">
                        Linked defect:{' '}
                        <button
                          type="button"
                          className="text-blue-800 font-semibold underline"
                          onClick={() => openDetail(detailQuery.data.linked.defect.id)}
                        >
                          {detailQuery.data.linked.defect.form_short} ·{' '}
                          {detailQuery.data.linked.defect.issued_date_display}
                        </button>
                      </p>
                    )}
                  </>
                )}
              </section>

              {phase === 'detail' && (
                <>
                  <section className="bg-white rounded-xl border border-gray-200 p-5">
                    <h2 className="text-xs font-bold uppercase text-slate-500 mb-2">Log</h2>
                    <ul className="divide-y divide-gray-100">
                      {(detailQuery.data.log || []).map((ev, i) => (
                        <li key={i} className="py-2">
                          <p className="text-xs font-semibold text-slate-500">
                            {ev.at_display} · {ev.action}
                          </p>
                          <p className="text-sm text-gray-800 mt-0.5">{ev.detail}</p>
                        </li>
                      ))}
                      {!detailQuery.data.log?.length && (
                        <li className="text-sm text-gray-500 py-2">No log entries.</li>
                      )}
                    </ul>
                  </section>
                  <button
                    type="button"
                    onClick={() => setPhase('preview')}
                    className="px-5 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-semibold"
                  >
                    Preview
                  </button>
                </>
              )}

              {phase === 'preview' && (
                <section className="space-y-3">
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden min-h-[420px]">
                    {previewHtml ? (
                      <iframe title="Form preview" className="w-full min-h-[520px] border-0" srcDoc={previewHtml} />
                    ) : (
                      <p className="p-8 text-center text-gray-500 text-sm">Loading preview…</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 items-end">
                    <button
                      type="button"
                      onClick={() => setPhase('detail')}
                      className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm font-semibold"
                    >
                      Back
                    </button>
                    <div className="flex flex-wrap gap-2 items-end flex-1">
                      <input
                        type="email"
                        value={sendTo}
                        onChange={(e) => setSendTo(e.target.value)}
                        placeholder="Recipient email"
                        className="rounded-lg border border-gray-200 px-3 py-2.5 text-sm min-w-[220px] flex-1"
                      />
                      <button
                        type="button"
                        disabled={sendBusy}
                        onClick={handleSend}
                        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-blue-700 text-white text-sm font-semibold disabled:opacity-60"
                      >
                        <Send className="h-4 w-4" /> Send
                      </button>
                      <button
                        type="button"
                        onClick={handlePrint}
                        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-slate-800 text-white text-sm font-semibold"
                      >
                        <Printer className="h-4 w-4" /> Print
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">Send or Print returns to the Issued document search screen.</p>
                </section>
              )}
            </>
          )}
          {actionMsg && <p className="text-sm text-amber-900 bg-amber-50 rounded-lg p-3">{actionMsg}</p>}
        </div>
      )}

      {ssOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSsOpen(false);
          }}
        >
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col shadow-xl">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Select SS</h2>
              <button
                type="button"
                className="text-sm font-semibold text-blue-800"
                onClick={() => setSsOpen(false)}
              >
                Done
              </button>
            </div>
            <div className="overflow-y-auto px-4 py-2 flex-1">
              {sortedSsCatalog.map((s) => {
                const code = s.code || s.ss_code;
                const on = ssCodes.includes(code);
                return (
                  <label key={code} className="flex items-start gap-2 py-2 border-b border-gray-100">
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggleSs(code)}
                      className="mt-1 accent-blue-700"
                    />
                    <span className="text-sm">
                      <span className="font-semibold text-gray-900">{code}</span>
                      <span className="block text-xs text-gray-500">{s.name}</span>
                    </span>
                  </label>
                );
              })}
              {!sortedSsCatalog.length && (
                <p className="text-sm text-gray-500 py-6 text-center">No SS catalogue loaded.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
